import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { trackEvent } from '../utils/consent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";

// Fire purchase events at most once per checkout session (survives reload + retry polling)
function fireConversionOnce(key, fn) {
  try {
    const flag = `mdd_tracked_${key}`;
    if (localStorage.getItem(flag)) return;
    localStorage.setItem(flag, '1');
    fn();
  } catch { /* ignore */ }
}

export default function UpgradeSuccess() {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  const sessionId = searchParams.get('session_id');
  const isVip = searchParams.get('vip') === 'true';
  const isTrial = searchParams.get('trial') === 'true';

  useEffect(() => {
    if (isVip) {
      setStatus('success');
      refreshUser();
      fireConversionOnce('vip_' + (Date.now()), () => {
        trackEvent('Subscribe', { plan: 'vip', value: 0, currency: 'GBP' });
      });
    } else if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('failed');
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const checkPaymentStatus = async () => {
    if (attemptsRef.current >= 8) {
      setStatus('failed');
      return;
    }

    attemptsRef.current += 1;

    try {
      const { data } = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);

      if (data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        // Fire conversion event ONCE per session_id (deduped via localStorage)
        fireConversionOnce(sessionId, () => {
          const plan = data.plan;
          const value = typeof data.amount === 'number' ? data.amount : 0;
          const currency = (data.currency || 'GBP').toUpperCase();
          if (plan === 'trial') {
            trackEvent('StartTrial', { value, currency, plan: 'trial' });
          } else {
            trackEvent('Subscribe', { value, currency, plan: plan || 'unknown' });
          }
        });
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }
    } catch (err) {
      // If auth error (401/403), stop retrying — user needs to check settings
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        setStatus('failed');
        return;
      }
    }

    // Retry after 2.5 seconds
    timerRef.current = setTimeout(checkPaymentStatus, 2500);
  };

  return (
    <div className="min-h-screen bg-page flex flex-col items-center justify-center px-4">
      <img src={LOGO_URL} alt="My Direct Debits" className="h-16 w-16 mb-8" />

      {status === 'checking' && (
        <div className="text-center animate-fade-in">
          <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
          <h1 className="font-heading font-bold text-dark text-2xl mb-2">
            Processing Payment...
          </h1>
          <p className="text-text-secondary">
            Please wait while we confirm your payment
          </p>
        </div>
      )}

      {status === 'success' && (
        <div className="text-center animate-fade-in">
          <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading font-bold text-dark text-3xl mb-2">
            {isTrial ? "Trial Activated!" : "You're Premium!"}
          </h1>
          <p className="text-text-secondary mb-8 max-w-sm">
            {isTrial
              ? "You now have unlimited access for 30 days. Enjoy all premium features!"
              : "You now have full access to unlimited direct debits and all premium features."}
          </p>
          <Link
            to="/dashboard"
            data-testid="continue-to-app-btn"
            className="btn-primary inline-block px-8 py-3 text-lg"
          >
            Continue to App
          </Link>
        </div>
      )}

      {status === 'failed' && (
        <div className="text-center animate-fade-in">
          <div className="w-20 h-20 bg-alert-light rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-alert" />
          </div>
          <h1 className="font-heading font-bold text-dark text-2xl mb-2">
            Payment Not Confirmed
          </h1>
          <p className="text-text-secondary mb-8 max-w-sm">
            If you completed payment, don't worry — your money is safe. Go to Settings and tap "Restore Premium" to activate your subscription.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/settings"
              data-testid="restore-btn"
              className="btn-primary px-6 py-3"
            >
              Go to Settings
            </Link>
            <Link
              to="/dashboard"
              className="btn-secondary px-6 py-3"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
